/**
 * @file useSliderVerify.js
 * @description 滑块人机验证
 * @module shared/composables/useSliderVerify
 */

import { onBeforeUnmount, onMounted, ref } from 'vue'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'

/**
 * @param {(event: 'verify-success') => void} emit
 */
export function useSliderVerify(emit) {
  const trackRef = ref(null)
  const sliderLeft = ref(0)
  const isDragging = ref(false)
  const startX = ref(0)
  const trackWidth = ref(0)
  const isVerified = ref(false)
  const showHint = ref(false)
  const hintText = ref('请拖动滑块完成验证')

  function initTrackWidth() {
    if (trackRef.value) {
      const sliderWidth = isMobileLayoutViewport() ? 36 : 40
      trackWidth.value = trackRef.value.offsetWidth - sliderWidth
    }
  }

  function updateSliderPosition(clientX) {
    const newLeft = clientX - startX.value
    if (newLeft < 0) {
      sliderLeft.value = 0
    } else if (newLeft > trackWidth.value) {
      sliderLeft.value = trackWidth.value
      if (!isVerified.value) {
        verifySuccess()
      }
    } else {
      sliderLeft.value = newLeft
    }
  }

  function resetSlider() {
    sliderLeft.value = 0
    showHint.value = true
    hintText.value = '验证失败，请重试'
    setTimeout(() => {
      showHint.value = false
    }, 2000)
  }

  function verifySuccess() {
    isVerified.value = true
    if (trackRef.value) {
      sliderLeft.value = trackRef.value.offsetWidth
    }
    showHint.value = true
    hintText.value = '验证通过!'
    emit('verify-success')
  }

  function handleMouseDown(e) {
    if (isVerified.value) return
    isDragging.value = true
    startX.value = e.clientX - sliderLeft.value
    e.preventDefault()
  }

  function handleTouchStart(e) {
    if (isVerified.value) return
    isDragging.value = true
    startX.value = e.touches[0].clientX - sliderLeft.value
    e.preventDefault()
  }

  function handleMouseMove(e) {
    if (!isDragging.value || isVerified.value) return
    updateSliderPosition(e.clientX)
  }

  function handleTouchMove(e) {
    if (!isDragging.value || isVerified.value) return
    updateSliderPosition(e.touches[0].clientX)
  }

  function handleMouseUp() {
    if (isDragging.value && !isVerified.value) {
      if (sliderLeft.value < trackWidth.value - 5) {
        resetSlider()
      }
    }
    isDragging.value = false
  }

  function handleTouchEnd() {
    if (isDragging.value && !isVerified.value) {
      if (sliderLeft.value < trackWidth.value - 5) {
        resetSlider()
      }
    }
    isDragging.value = false
  }

  function reset() {
    sliderLeft.value = 0
    isVerified.value = false
    isDragging.value = false
    showHint.value = false
  }

  onMounted(() => {
    initTrackWidth()
    window.addEventListener('resize', initTrackWidth)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove)
    window.addEventListener('touchend', handleTouchEnd)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', initTrackWidth)
    window.removeEventListener('mousemove', handleMouseMove)
    window.removeEventListener('mouseup', handleMouseUp)
    window.removeEventListener('touchmove', handleTouchMove)
    window.removeEventListener('touchend', handleTouchEnd)
  })

  return {
    trackRef,
    sliderLeft,
    isDragging,
    isVerified,
    showHint,
    hintText,
    handleMouseDown,
    handleTouchStart,
    reset,
  }
}
